import {
  LayoutDashboard, Users, Building2, Target, FileText, Receipt,
  CreditCard, Wallet, FolderKanban, ListTodo, Package, Warehouse,
  Truck, ShoppingCart, Wrench, Shield, UserCog, Workflow, BarChart3,
  Settings, ChevronDown, ChevronRight, BookOpen, FileX2, IndianRupee,
  Factory, Crown, Server, ScrollText, Sparkles, CalendarDays, Coins,
  ClipboardList, FileSearch, Ship, Hammer, Download, Phone,
  MapPin, Box, ScanBarcode, FileOutput, FileSignature, AlertTriangle,
  Scale, Tags, Gauge, ArrowRightLeft, ArrowDownToLine, ArrowUpFromLine, Bot, Plug,
  Activity, Percent, CheckCircle, ClipboardCheck, ShieldCheck,
  Headphones, Handshake, Calculator, FlaskConical, Route, Gift,
  TrendingUp, Globe, Briefcase, Camera, CalendarCheck, CalendarClock, KeyRound, Mic,
  Trophy, Award, BellRing, Smartphone, GraduationCap,
} from 'lucide-react';
import { DEALER_PAGE_ROLES, type DealerTier } from '../../lib/roles';
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
type Icon = typeof LayoutDashboard;

export interface NavItem {
  to: string;
  icon: Icon;
  label: string;
  /** Module key used for tenant-level feature flags */
  module?: string;
  /** Inline count badge (optional, future use) */
  count?: number;
  /** Shown only if user.role.name matches (legacy gate — e.g. 'telecaller', 'admin') */
  roleNamesOnly?: string[];
  /**
   * Plan tier gate — item is hidden for tenants below this tier.
   * Supported values: 'ENTERPRISE' | 'GROWTH' | 'STARTER'.
   * Checked against tenant.planTier in filterItems.
   */
  planGate?: 'ENTERPRISE' | 'GROWTH' | 'STARTER';
  /**
   * LBR2-G1 — per-tenant capability gate. Item is hidden unless
   * tenant.capabilities[capability] === true. Used to keep SS-only surfaces
   * (recruitment, discount-slab UI) off other tenants' nav.
   */
  capability?: 'discountUi' | 'recruitment';
  /**
   * Phase 5 (2026-07-29) — hide this entry unless the caller holds this
   * permission. Undefined means always show. Purely an affordance: the route
   * and the API are still gated server-side, and this only stops us offering a
   * link that would 403 on arrival.
   *
   * 🔒 Fails CLOSED — `useMyPermissions` reports everything false while loading
   * and on error, so an entry appears late rather than appearing wrongly.
   */
  requiresPermission?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
  /** Items in the expandable "See more" drawer (D-11) */
  seeMore?: NavItem[];
}

export type SidebarView =
  | 'unknown'
  | 'crm_operations'
  | 'qa_manager'
  | 'qa_inspector'
  | 'dispatch_manager'
  | 'loader'
  | 'security'
  | 'puf_operator'
  | 'packing_operator'
  | 'owner'
  | 'sales'
  | 'accountant'
  | 'production'
  | 'inventory'
  | 'procurement'
  | 'project'
  | 'service'
  | 'telecaller'
  | 'hr'
  | 'lead_manager'
  | 'lead_triage'
  | 'super_admin'
  // Executive views (Apr 22, 2026 exec sprint — trimmed 3–5-item Core)
  | 'sales_executive'
  | 'service_technician'
  | 'production_operator'
  | 'warehouse_executive'
  | 'accounts_executive'
  | 'field_partner'
  // Apr 26, 2026 (Squad-B) — bifurcation re-introduced. Field Executive sees
  // own/assigned leads only; Backend Staff (tele sales) sees their team's
  // leads. The legacy `sales_executive` view is preserved for back-compat.
  | 'field_executive'
  | 'backend_staff';

export const VIEW_LABELS: Record<SidebarView, string> = {
  unknown: 'No access',
  crm_operations: 'CRM Operations',
  qa_manager: 'QA Manager',
  qa_inspector: 'QA Inspector',
  dispatch_manager: 'Dispatch Manager',
  loader: 'Loader',
  security: 'Security Guard',
  puf_operator: 'PUF Operator',
  packing_operator: 'Packing Operator',

  owner: 'Owner / Admin',
  sales: 'Sales Manager',
  accountant: 'Accountant',
  production: 'Production',
  inventory: 'Inventory',
  procurement: 'Procurement',
  project: 'Projects',
  service: 'Service',
  telecaller: 'TeleCaller',
  hr: 'Human Resources',
  lead_manager: 'Lead Manager',
  lead_triage: 'Lead Triage',
  super_admin: 'Platform',
  sales_executive: 'Sales Executive',
  service_technician: 'Service Technician',
  production_operator: 'Production Operator',
  warehouse_executive: 'Warehouse Executive',
  accounts_executive: 'Accounts Executive',
  field_partner: 'Field Partner',
  field_executive: 'Field Executive',
  backend_staff: 'Backend / Tele Sales',
};

/* ------------------------------------------------------------------ */
/*  Per-role sidebar configurations — D-11 Core + See-more             */
/* ------------------------------------------------------------------ */

/** Admin / Owner — D-07 core 9 + See-More */
const OWNER_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/ai', icon: Sparkles, label: 'AI Insights', module: 'ai' },
    ],
  },
  {
    // Owner retains team and company oversight after the 2026-09-16 amendment.
    label: 'Performance',
    items: [
      { to: '/scorecard/team', icon: BarChart3, label: 'Team Scorecards' },
      { to: '/scorecard/org', icon: Gauge, label: 'Oversight' },
      { to: '/pipeline', icon: Route, label: 'Pipeline' },
      { to: '/lead-ops', icon: ClipboardList, label: 'Lead Ops' },
      { to: '/sales-targets', icon: TrendingUp, label: 'Sales Targets' },
    ],
  },
  {
    label: 'Revenue',
    items: [
      // Apr 27 (Kunwar BUG-15) — Leads must be reachable from Owner sidebar.
      // Was previously only in SALES_SECTIONS, invisible to admin.
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      // Field Sessions Phase 2 (2026-07-29). 🔒 Ungated on purpose: availability
      // is EVERY sales person — managers go out too, and their day is mostly
      // meetings rather than cold capture (spec §1).
      { to: '/field-sessions', icon: MapPin, label: 'Field Sessions' },
      // Field Agents Phase 4 (2026-07-29) — the MANAGER board.
      // 🔒 Phase 5: `requiresPermission` is what keeps this manager-facing —
      // the rep sidebar is left with Field Sessions alone because the entry is
      // filtered out for anyone without `view_field_agents`, not because of
      // which array it sits in. `view_field_agents` is OFF for every role until
      // an admin ticks it, so until then nobody sees this at all (previously it
      // was shown to everyone and 403'd on arrival).
      { to: '/field-agents', icon: Users, label: 'Field Agents', requiresPermission: 'view_field_agents' },
      // Phase 5 (2026-07-29) — the TA/DA rate card. Gated on `approve_ta_da`
      // because setting the rates is the same authority as approving claims
      // against them (ta-da.routes.ts PUT /rates). Mirrored into SALES for the
      // same reason Team Tracking is: SALES_HEAD/SALES_MANAGER resolve to the
      // 'sales' view, not 'owner', so an owner-only entry would be unreachable
      // for the very managers who hold the permission.
      { to: '/admin/field-ta-da-rates', icon: IndianRupee, label: 'Travel Rates', requiresPermission: 'approve_ta_da' },
      // Jun 28 2026 — preserve the original Deepika Lead Triage Dashboard as a
      // legacy view as the new KPI dashboards roll in. Same /leads/triage route
      // + LeadTriageDashboard page; never displaced.
      { to: '/leads/triage', icon: ClipboardList, label: 'Triage Legacy', module: 'lead-integrations' },
      // (2026-09-18) Discount Approvals and Access Requests moved OUT of Revenue
      // into their own REQUESTS group below — both are approval queues, not
      // revenue reporting, and Manak wanted them found together.
      // L208 Squad D (br3-l208-coverage, 2026-07-17) — Dealer Coverage dashboard
      // (active-dealer cadence / R-A-G health / GPS field activity, separate from
      // B2C KPIs). Manager/owner; RequireRoles-gated in App.tsx + backend
      // /dealer-coverage manager/owner gate.
      { to: '/dealer-coverage', icon: Gauge, label: 'Dealer Coverage', module: 'lead-integrations', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager'] },
      // L175 / FW1 (2026-07-14) — Dealer Management screen.
      // L239 (2026-09-03): open to admin + lead_triage_coordinator + the manager
      // tier (managers see only their own dealers — backend dealer-access.ts
      // tiers enforce it). roleNamesOnly reads the shared lib/roles
      // DEALER_PAGE_ROLES, the same list as the App.tsx RequireRoles gate.
      // Label stays one string ('Dealers'); the page title changes per tier.
      { to: '/dealer-management', icon: Handshake, label: 'Dealers', module: 'lead-integrations', roleNamesOnly: DEALER_PAGE_ROLES },
      { to: '/invoices', icon: Receipt, label: 'Invoices', module: 'finance' },
      { to: '/analytics', icon: BarChart3, label: 'Forecasting', module: 'analytics' },
    ],
  },
  {
    /**
     * REQUESTS (2026-09-18, Manak) — the two approval queues an admin is asked
     * to action, in one place. Both were previously buried in Revenue between
     * Leads and Invoices, where they read as reporting rather than a to-do.
     *
     * Role gates are carried over UNCHANGED from the Revenue entries, so nobody
     * gains or loses access by this move — it is purely where the link sits.
     *
     * 'Discount Requests' matches the route (/discount-requests), the page
     * (DiscountRequestsPage) and the label the SALES view already uses; the old
     * owner-only 'Discount Approvals' wording was the odd one out.
     */
    label: 'Requests',
    items: [
      // MONEY/approval — pending discount-approval queue. Route is also guarded
      // by RequireApproverRole; the page defaults to the pending filter.
      // CVG3-DISC (L103, 2026-07-11) — + coordinator/sales_coordinator (read/
      // track view; approve/reject stays manager+ via the page-level gate).
      // sr_sales_manager/manager_sales deliberately NOT added: those roles
      // resolve to the SALES view and reach /discount-requests there.
      // 2026-09-25 (Manak): + operations_manager — Sugandha tracks the queue (read-only card; decide/escalate gates unchanged).
      { to: '/discount-requests', icon: Percent, label: 'Discount Requests', module: 'sales', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'coordinator', 'sales_coordinator', 'operations_manager'] },
      // L6/SPEC-REASSIGN (2026-07-08) — admin review queue. ONE page serving
      // BOTH queues: lead/customer access + reassignment requests
      // (GET /lead-capture/customer-access-requests) AND the dealer access
      // requests reps raise from the app (DealerAccessRequestsSection ->
      // GET /dealer-access-requests, the Sales Round B surface). Gate matches
      // the backend requireRole on the customer-access-requests endpoints.
      { to: '/admin/access-requests', icon: KeyRound, label: 'Access Requests', module: 'lead-integrations', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager'] },
    ],
  },
  {
    // Track B (2026-07-03) — admin "Team Tracking" map: live rep positions +
    // day trail + geofence CRUD. Same role gate as Discount Approvals above
    // (admin/owner + manager-tier "team head" roles) — this is a supervisory
    // surface, not something every rep should see in their own sidebar.
    // AI-5bz2 (2026-07-04): added operations_manager — the geofence API
    // (geofence.routes.ts) already write-permits operations_manager, but the
    // sidebar entry was missing the role so they had no way to reach the page.
    label: 'Team',
    items: [
      { to: '/team-tracking', icon: MapPin, label: 'Team Tracking', module: 'hr', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager'] },
      // Attendance Admin (2026-07-04) — HR month-matrix + override surface.
      // Same supervisory gate as Team Tracking PLUS the net-new 'hr' role
      // (seeded 2026-07-04; backend gate: attendance-admin.routes.ts).
      { to: '/attendance-admin', icon: CalendarCheck, label: 'Attendance', module: 'hr', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager', 'hr'] },
      // 2026-10-07 (Manak) Apps & Training — latest APKs + App Tour progress. HR + admin only
      // (backend /hr/app-overview/* re-checks the role).
      { to: '/hr/mobile-apps', icon: Smartphone, label: 'Mobile Apps', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'] },
      { to: '/hr/app-tour-progress', icon: GraduationCap, label: 'App Tour Progress', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'] },
      // CVG3-FU (L106, 2026-07-11) — team follow-ups dashboard (KPIs, filters,
      // reassign/complete/cancel). Supervisory gate incl. the SS team-head
      // role literals (sr_sales_manager/manager_sales); backend list is
      // getVisibleUserIds-scoped so team heads only ever see their team.
      { to: '/admin/follow-ups', icon: BellRing, label: 'Follow-ups', module: 'crm', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'sr_sales_manager', 'manager_sales', 'operations_manager'] },
      // Plan-2 Lane D (2026-07-19) — Meetings admin page (web view over the
      // Phase-5 /meetings/scheduled backend: reps' scheduled meetings, KPI
      // strip, create/reschedule). Same supervisory gate family as Follow-ups
      // above; App.tsx RequireRoles mirrors this list + super_admin.
      { to: '/meetings', icon: CalendarClock, label: 'Meetings', module: 'crm', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'sr_sales_manager', 'manager_sales', 'operations_manager'] },
      // QA2-WEB-4 (L32 + L28, 2026-07-08) — admin Call Recordings dashboard:
      // all rep call logs + playable recordings + transcripts + made/recorded
      // counts. STRICTLY admin/owner (L28: recording UI is never revealed to
      // employees — no manager/sales_manager here, unlike the rows above).
      // Route is also RequireRoles-gated in App.tsx; backend /admin/call-logs
      // (contract C7) enforces admin/owner server-side.
      { to: '/admin/call-recordings', icon: Mic, label: 'Call Recordings', roleNamesOnly: ['owner', 'admin'] },
      // QA3-WEB-PERF (L52/L57/L58, 2026-07-09) — supervisory performance +
      // fleet-health dashboards. STRICTLY admin/owner (Team Performance
      // embeds the recordings/transcripts panel — L28 rule — and the report
      // card exposes cross-employee weighted KPIs). Routes are also
      // RequireRoles-gated in App.tsx. NOTE: 'Team Performance' here is the
      // NEW admin scorecard (/admin/team-performance), distinct from the
      // telecaller /team-performance page; 'System Health' here is the
      // FIELD-APP fleet view (/admin/system-health), distinct from the
      // server-infra /admin/health entry in the Org see-more below.
      { to: '/admin/team-performance', icon: Trophy, label: 'Team Performance', roleNamesOnly: ['owner', 'admin'] },
      { to: '/admin/employee-report', icon: Award, label: 'Report Card', roleNamesOnly: ['owner', 'admin'] },
      // Piece 2 Option B (2026-08-12) — bulk sales-targets matrix (owner/admin;
      // writes gated ops/ceo at the backend). Sits with its report siblings.
      { to: '/admin/sales-targets', icon: Target, label: 'Sales Targets', roleNamesOnly: ['owner', 'admin'] },
      // L201 (2026-07-15) — Call KPI monitoring: connected calls bucketed by
      // talk time (0-30s / 30-60s / 60s+) org-wide + per rep. Same admin/owner
      // gate as its two siblings above and for the same reason: it exposes
      // cross-employee call performance. A VIEW over the existing
      // GET /telecaller/call-stats (KD6) — no new backend.
      { to: '/admin/call-kpi', icon: Phone, label: 'Call KPIs', roleNamesOnly: ['owner', 'admin'] },
      { to: '/admin/system-health', icon: Activity, label: 'System Health', roleNamesOnly: ['owner', 'admin'] },
    ],
  },
  {
    // L229 (2026-07-19) — Support Desk umbrella. TWO distinct ticket systems:
    //  • System Support (/support/tickets → support_tickets) — INTERNAL bug reports
    //    + feature requests about the AURA software; desk roles respond.
    //  • Service Tickets (/service-tickets → service_tickets) — CUSTOMER-FACING
    //    field service / AMC / warranty (service-mgmt module; canonical
    //    /api/v1/service-tickets backend, unaffected by the L229 alias removal).
    // Reporters reach their own system-support tickets via avatar/More → Support.
    label: 'Support Desk',
    items: [
      { to: '/support/tickets', icon: Headphones, label: 'System Support', roleNamesOnly: ['owner', 'admin', 'super_admin'] },
      { to: '/support/bug-tests', icon: FlaskConical, label: 'Bug Test Dashboard', roleNamesOnly: ['owner', 'admin', 'super_admin'] },
      { to: '/service-tickets', icon: Wrench, label: 'Service Tickets', module: 'service-mgmt', roleNamesOnly: ['owner', 'admin', 'super_admin'] },
    ],
  },
  {
    // Apr 27 (Kunwar BUG-14) — CRM customer-record surface added to Owner.
    // Sun Stellar's owner needs Contacts as a top-level group; the WhatsApp
    // lead sync from ChatKart creates Contact rows here that must be visible.
    label: 'CRM',
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/customers', icon: Building2, label: 'Customers', module: 'crm' },
      { to: '/accounts', icon: FolderKanban, label: 'Accounts', module: 'accounts' },
      // v6.3.7 — Sun Stellar Pool Heat Pump Calculator. Originally placed
      // in CRM `seeMore` but the renderer only displays seeMore on the LAST
      // section (Sidebar.tsx:968 `isLast` guard), so non-last seeMore entries
      // were silently dropped. Promoted to a top-level item — only renders
      // for tenants with 'pool_heat_pump_calculator' in enabledModules.
      // Calculator itself is NOT free; this entry opens the Lead picker
      // (MeetingModeLauncher) which then binds a session.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
    seeMore: [
      { to: '/activities', icon: CalendarDays, label: 'Activities' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/sales-orders', icon: ShoppingCart, label: 'Orders', module: 'sales' },
      { to: '/manufacturing/dashboard', icon: Factory, label: 'Production', module: 'manufacturing' },
      { to: '/inventory', icon: Warehouse, label: 'Inventory', module: 'warehouse' },
      { to: '/warehouse/gate-passes', icon: ShieldCheck, label: 'Gate passes', module: 'warehouse' },
      { to: '/product-catalogue', icon: Package, label: 'Product Catalogue', module: 'warehouse' },
      { to: '/service-tickets', icon: Wrench, label: 'Service', module: 'service-mgmt' },
    ],
    // Apr 27 (Kunwar BUG-14 + sweep) — Owner sidebar was missing most ops
    // surfaces. Adding See-More so every major function is ≤ 1 click away
    // without overcrowding the visible core.
    seeMore: [
      { to: '/projects', icon: Briefcase, label: 'Projects' },
      { to: '/tasks', icon: ClipboardCheck, label: 'Tasks' },
      { to: '/proposals', icon: FileText, label: 'Proposals', module: 'sales' },
      { to: '/estimates', icon: FileText, label: 'Estimates', module: 'sales' },
      { to: '/quotation-jobs', icon: FileText, label: 'Quotations', module: 'procurement' },
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { to: '/vendors', icon: Building2, label: 'Vendors', module: 'procurement' },
      { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders', module: 'procurement' },
      // Sprint 2-3 (May 24 2026 — Agent KK) — vendor bills surface
      { to: '/purchase-invoices', icon: Receipt, label: 'Purchase Invoices', module: 'procurement' },
    ],
    seeMore: [
      { to: '/purchase-dashboard', icon: LayoutDashboard, label: 'Purchase Dashboard', module: 'procurement' },
      { to: '/purchase-requests', icon: FileText, label: 'Purchase Requests', module: 'procurement' },
      { to: '/vendor-rate-comparison', icon: BarChart3, label: 'Vendor Rates', module: 'procurement' },
      { to: '/products', icon: Package, label: 'Products', module: 'warehouse' },
    ],
  },
  {
    label: 'Books',
    items: [
      { to: '/accounting?tab=ledger', icon: BookOpen, label: 'Ledger', module: 'accounting' },
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
      // Phase 2 plan 09 — N2 money gate on every dispatch form (accounts_approver maps to this view via plan 01 roleLayouts)
      { to: '/finance/dispatch-approvals', icon: ShieldCheck, label: 'Dispatch approvals', module: 'finance' },
    ],
    seeMore: [
      // Sprint 2-3 (May 24 2026 — Agent LL) — Tally-grade ledger pack
      { to: '/ledger/vouchers', icon: ClipboardList, label: 'Voucher Register', module: 'accounting' },
      { to: '/ledger/trial-balance', icon: Scale, label: 'Trial Balance', module: 'accounting' },
      { to: '/ledger/profit-loss', icon: BarChart3, label: 'Profit & Loss', module: 'accounting' },
      { to: '/ledger/balance-sheet', icon: Calculator, label: 'Balance Sheet', module: 'accounting' },
      // === GSTPACK-TLY ===
      { to: '/tally-export', icon: FileOutput, label: 'Tally Export', module: 'accounting' },
      // Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON)
      { to: '/accounting/suite/reports', icon: BarChart3, label: 'Financial Reports', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      { to: '/accounting/suite/payments', icon: CreditCard, label: 'Payments Suite', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      { to: '/accounting/suite/period', icon: Calculator, label: 'Period Close', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      { to: '/gst/filing', icon: FileOutput, label: 'GST Filing', module: 'finance' },
      // === GSTPACK-GS9 === GSTR-9 annual return (2026-07-19)
      { to: '/gst/gstr9', icon: FileOutput, label: 'GSTR-9 Annual', module: 'finance' },
      { to: '/gst/e-invoices', icon: FileSignature, label: 'e-Invoice', module: 'finance' },
      { to: '/gst/e-waybills', icon: Truck, label: 'e-Way Bill', module: 'finance' },
      { to: '/credit-notes', icon: FileX2, label: 'Credit Notes', module: 'finance' },
      { to: '/debit-notes', icon: ScrollText, label: 'Debit Notes', module: 'finance' },
      { to: '/payment-confirmations', icon: CheckCircle, label: 'Payment Confirmations', module: 'finance' },
      { to: '/finance/approvals', icon: ShieldCheck, label: 'Approvals', module: 'finance' },
    ],
  },
  {
    label: 'Org',
    items: [
      { to: '/employees', icon: UserCog, label: 'People', module: 'hr' },
      // LBR2-D3 (2026-07-26) — internal HR recruitment dashboard. roleNamesOnly
      // byte-identical to App.tsx /hr/recruitment RequireRoles.
      { to: '/hr/recruitment', icon: Briefcase, label: 'Recruitment', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'], capability: 'recruitment' },
      { to: '/hr/recruitment/partners', icon: Briefcase, label: 'Local partners', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'], capability: 'recruitment' },
      // L200 Sheet 1 (2026-07-16) — payroll admin. The ONLY way a human can reach
      // the payroll engine, which shipped fully wired with zero frontend.
      //
      // 🔒 roleNamesOnly is MANDATORY here, not optional polish. The `owner` view is
      // the DEFAULT FALLBACK for every role resolveRoleKey doesn't recognise
      // (roleLayouts.ts — final `return 'OWNER'`), so an ungated row would appear
      // for roles that App.tsx's RequireRoles then bounces to /dashboard — the
      // existing sidebar↔route-gate bug (cf. /attendance-admin :199 vs App.tsx:670).
      // This list must stay byte-identical to the route's RequireRoles, which
      // mirrors payroll/compensation-access.ts:20-26 (incl. `accountant`).
      { to: '/payroll', icon: IndianRupee, label: 'Payroll', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      { to: '/expense-approvals', icon: Wallet, label: 'Expense Approvals', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      { to: '/telecaller', icon: Phone, label: 'Telecaller', module: 'telecaller' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    // See-More for admin: deeper-ops items D-11 says live here
    seeMore: [
      { to: '/admin/settings', icon: Settings, label: 'AURA Settings' },
      // CVG3-NOTIF (L104 addendum, 2026-07-11) — announcement composer (pin +
      // pre/post-login placement). Admin section; icon reused (no new import line).
      { to: '/admin/announcements', icon: ScrollText, label: 'Announcements' },
      // Two-layer discount Task 7 (2026-07-04) — Layer-B designation×territory
      // matrix editor. Backend self-gates admin/owner/super_admin; this owner
      // see-more is the admin-gated section next to the other admin builders.
      { to: '/admin/discount-policies', icon: Percent, label: 'Discount Policies' },
      { to: '/admin/carrier-numbers', icon: Phone, label: 'Carrier Numbers' },
      { to: '/admin/installations', icon: Camera, label: 'Photo Focus' },
      { to: '/settings/ai-assistant', icon: Sparkles, label: 'AI Assistant', planGate: 'ENTERPRISE' },
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
      { to: '/admin/health', icon: Server, label: 'System Health' },
      { to: '/admin/resources', icon: Gauge, label: 'Resource Mgmt' },
      { to: '/admin/lead-routing', icon: Route, label: 'Lead Routing', roleNamesOnly: ['owner', 'admin', 'super_admin'] },
      { to: '/admin', icon: Crown, label: 'Super Admin' },
      { to: '/workflows', icon: Workflow, label: 'Workflows', module: 'automation' },
      { to: '/credits', icon: Coins, label: 'AI Credits', module: 'ai' },
      { to: '/billing/plans', icon: IndianRupee, label: 'Billing Plans' },
      { to: '/leave-requests', icon: CalendarDays, label: 'Leave Requests', module: 'hr' },
    ],
  },
];

/** Sales Manager — D-01 + D-02 core 8 + See-More */
const SALES_SECTIONS: NavSection[] = [
  {
    // K7 Scorecard — Jun 2026. Sales manager sees My + Team + Pipeline.
    //
    // AI-mojz (2026-08, Task 5) — 'Dashboard' lives here (as the FIRST item)
    // because the former standalone 'Pipeline' section collapsed to that one
    // child once 'Forecast' was removed above, leaving a section named
    // Pipeline whose only item wasn't Pipeline — AND colliding with the
    // 'Pipeline' item label three lines below (a section label may never
    // collide with an item label; the old item-labels-only test couldn't see
    // the collision because it never looked at section labels). Folding
    // Dashboard in here removes both problems in one move instead of just
    // renaming the item.
    label: 'Performance',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/scorecard', icon: Target, label: 'My Scorecard' },
      { to: '/scorecard/team', icon: BarChart3, label: 'Team Scorecards' },
      { to: '/pipeline', icon: Route, label: 'Pipeline' },
    ],
  },
  {
    label: 'CRM',
    items: [
      // AI-mojz (2026-08) — 'Customers' and 'Accounts' removed: Manak wants
      // them gone from the sales-manager nav, they compete with a manager's
      // actual work.
      { to: '/sales/activities', icon: CalendarDays, label: 'Activities' },
    ],
  },
  {
    label: 'Team',
    items: [
      { to: '/sales/reps', icon: Activity, label: 'Reps', module: 'crm' },
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      // Field Sessions Phase 2 (2026-07-29) — see the Owner note above.
      { to: '/field-sessions', icon: MapPin, label: 'Field Sessions' },
      // Field Agents Phase 4 (2026-07-29) — see the Owner note above. Phase 5
      // permission gate applies here identically.
      { to: '/field-agents', icon: Users, label: 'Field Agents', requiresPermission: 'view_field_agents' },
      // Phase 5 (2026-07-29) — the TA/DA rate card. Gated on `approve_ta_da`
      // because setting the rates is the same authority as approving claims
      // against them (ta-da.routes.ts PUT /rates). Mirrored into SALES for the
      // same reason Team Tracking is: SALES_HEAD/SALES_MANAGER resolve to the
      // 'sales' view, not 'owner', so an owner-only entry would be unreachable
      // for the very managers who hold the permission.
      { to: '/admin/field-ta-da-rates', icon: IndianRupee, label: 'Travel Rates', requiresPermission: 'approve_ta_da' },
      // v6.3.7 — Promoted from seeMore to top-level items so Mamta + sales.head
      // see the calculator entry without expanding See-more. Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
      // Track B (2026-07-03) — Sales view is where SALES_HEAD/SALES_MANAGER
      // role keys actually land (resolveRoleKey → 'sales', not 'owner' —
      // see resolveRoleKey mapping above), so the map/geofence surface must
      // be listed here too, not just in OWNER_SECTIONS, for manager-tier
      // "team head" users to see it.
      { to: '/team-tracking', icon: MapPin, label: 'Team Tracking', module: 'hr' },
      // Plan-2 Lane D (2026-07-19) — mirrored here (as Team Tracking is)
      // because SALES_HEAD/SALES_MANAGER role keys resolve to the 'sales'
      // view, not 'owner'. Manager-tier team heads must see their reps'
      // scheduled meetings. Route is RequireRoles-gated in App.tsx.
      { to: '/meetings', icon: CalendarClock, label: 'Meetings', module: 'crm' },
      // Attendance Admin (2026-07-04) — mirrored here for the same reason as
      // Team Tracking above (manager-tier role keys resolve to 'sales').
      { to: '/attendance-admin', icon: CalendarCheck, label: 'Attendance', module: 'hr', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager', 'hr'] },
      // L6/SPEC-REASSIGN (2026-07-08) — mirrored here (as Team Tracking /
      // Attendance are) because SALES_HEAD/SALES_MANAGER role keys resolve to
      // the 'sales' view, not 'owner'. Manager-tier approvers (Mamta/Ajay/Gyan)
      // must reach the access-request queue. Same gate as the backend requireRole.
      { to: '/admin/access-requests', icon: KeyRound, label: 'Access Requests', module: 'lead-integrations', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager'] },
      // L208 Squad D (br3-l208-coverage, 2026-07-17) — Dealer Coverage dashboard
      // (active-dealer cadence / R-A-G health / GPS field activity, separate from
      // B2C KPIs). Manager/owner; RequireRoles-gated in App.tsx + backend
      // /dealer-coverage manager/owner gate.
      { to: '/dealer-coverage', icon: Gauge, label: 'Dealer Coverage', module: 'lead-integrations', roleNamesOnly: ['owner', 'admin', 'manager', 'sales_manager', 'operations_manager'] },
      // L239 (2026-09-03) — Dealers page for the manager tier. Mirrored here
      // because SALES_HEAD/SALES_MANAGER role keys resolve to the 'sales' view,
      // not 'owner'. Managers see only their own dealers (backend
      // dealer-access.ts), hence the 'My dealers' label (spec L239 §3.1).
      // Same DEALER_PAGE_ROLES gate as OWNER_SECTIONS + App.tsx RequireRoles.
      { to: '/dealer-management', icon: Handshake, label: 'My dealers', module: 'lead-integrations', roleNamesOnly: DEALER_PAGE_ROLES },
    ],
    seeMore: [
      { to: '/samples', icon: FlaskConical, label: 'Samples' },
      { to: '/deal-closure', icon: Handshake, label: 'Deal Closure' },
      { to: '/discount-requests', icon: Percent, label: 'Discount Requests', module: 'sales' },
      { to: '/proposal-builder', icon: Settings, label: 'Proposal Builder', module: 'sales' },
      { to: '/proposals', icon: FileText, label: 'Proposals', module: 'sales' },
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
    ],
  },
];

/** Accountant / Account Manager — D-03 core 9 + See-More */
const ACCOUNTANT_SECTIONS: NavSection[] = [
  {
    label: 'Books',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/accounting?tab=ledger', icon: BookOpen, label: 'Ledger', module: 'accounting' },
      // Sprint 2-3 (May 24 2026 — Agent LL) — Vouchers now point at the new
      // dedicated Voucher Register page instead of the legacy tabbed view.
      { to: '/ledger/vouchers', icon: ClipboardList, label: 'Vouchers', module: 'accounting' },
      { to: '/invoices', icon: Receipt, label: 'Invoices', module: 'finance' },
      // Phase 2 plan 09 — N2 money gate on every dispatch form (accounts_approver maps to this view via plan 01 roleLayouts)
      { to: '/finance/dispatch-approvals', icon: ShieldCheck, label: 'Dispatch approvals', module: 'finance' },
      // Sprint 2-3 (May 24 2026 — Agent KK) — vendor bills surface
      { to: '/purchase-invoices', icon: Receipt, label: 'Purchase Invoices', module: 'finance' },
      { to: '/payment-confirmations', icon: CheckCircle, label: 'Payment Confirmations', module: 'finance' },
      // L200 Sheet 1 (2026-07-16) — payroll admin. `accountant` IS in the engine's
      // audience (compensation-access.ts:20-26 → payroll.routes.ts:52); the spec's
      // §11 role list omitted it and was wrong (C5). This is the view the
      // `accountant` role actually resolves to (roleLayouts.ts → FINANCE_MANAGER
      // → Sidebar roleKeyToView → 'accountant'), so without this row an accountant
      // would have a working /payroll route and no way to reach it.
      //
      // 🔒 roleNamesOnly is MANDATORY: FINANCE_MANAGER is also reachable by
      // DESIGNATION (title contains 'accountant'/'finance manager'/'cfo'/'finance
      // head'), so e.g. a sales_rep titled "Finance Manager" lands on this view.
      // The gate keeps the row byte-identical to the route's RequireRoles.
      { to: '/payroll', icon: IndianRupee, label: 'Payroll', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      { to: '/expense-approvals', icon: Wallet, label: 'Expense Approvals', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      // Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON) — payments/recon
      // doctypes + period close. roleNamesOnly byte-identical to the routes'
      // RequireRoles (no `hr`: GL/payments are not an HR surface).
      { to: '/accounting/suite/payments', icon: CreditCard, label: 'Payments Suite', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      { to: '/accounting/suite/period', icon: Calculator, label: 'Period Close', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/gst/filing', icon: FileOutput, label: 'GST Filing', module: 'finance' },
      // === GSTPACK-G2B === (2026-07-19)
      { to: '/gst/gstr2b', icon: FileSearch, label: '2B Reconciliation', module: 'finance' },
      { to: '/gst/e-invoices', icon: FileSignature, label: 'e-Invoice', module: 'finance' },
      { to: '/gst/e-waybills', icon: Truck, label: 'e-Way Bill', module: 'finance' },
    ],
  },
  {
    label: 'Reports',
    items: [
      // Sprint 2-3 (May 24 2026 — Agent LL) — Tally-grade reports replace
      // the legacy ?tab= aliases. Old routes still resolve (kept in seeMore
      // as fallbacks until users are off muscle memory).
      { to: '/ledger/trial-balance', icon: Scale, label: 'Trial Balance', module: 'accounting' },
      { to: '/ledger/profit-loss', icon: BarChart3, label: 'P&L', module: 'accounting' },
      { to: '/ledger/balance-sheet', icon: Calculator, label: 'Balance Sheet', module: 'accounting' },
      { to: '/accounting?tab=outstanding', icon: CreditCard, label: 'Outstanding', module: 'accounting' },
      // Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON) — server-computed
      // reports hub (TB/P&L/BS/cash-flow/AR/AP/general-ledger/ITC-04).
      { to: '/accounting/suite/reports', icon: BarChart3, label: 'Financial Reports', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
    ],
    seeMore: [
      { to: '/finance/approvals', icon: ShieldCheck, label: 'Financial Approvals', module: 'finance' },
      { to: '/finance/security-deposits', icon: Shield, label: 'Security Deposits', module: 'finance' },
      { to: '/finance/invoice-swap', icon: ArrowRightLeft, label: 'Invoice Swap', module: 'finance' },
      { to: '/credit-notes', icon: FileX2, label: 'Credit Notes', module: 'finance' },
      { to: '/debit-notes', icon: ScrollText, label: 'Debit Notes', module: 'finance' },
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
      // Legacy tabbed reports — kept as fallbacks during the May 24 cutover
      { to: '/accounting?tab=pl', icon: BarChart3, label: 'Legacy P&L (tab)', module: 'accounting' },
      { to: '/accounting?tab=bs', icon: Calculator, label: 'Legacy Balance Sheet (tab)', module: 'accounting' },
    ],
  },
];

/** Production Manager — D-04 core 7 + See-More */
const PRODUCTION_SECTIONS: NavSection[] = [
  {
    label: 'Shop Floor',
    items: [
      { to: '/manufacturing/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'manufacturing' },
      { to: '/production', icon: Factory, label: 'Production Orders', module: 'manufacturing' },
      { to: '/manufacturing/boms', icon: Hammer, label: 'BOM Dashboard', module: 'manufacturing' },
    ],
  },
  {
    label: 'Quality',
    items: [
      { to: '/manufacturing/qc', icon: ClipboardCheck, label: 'QC', module: 'manufacturing' },
      { to: '/manufacturing/lot-approvals', icon: ClipboardCheck, label: 'Lot approvals', module: 'manufacturing' },
      { to: '/rejections', icon: AlertTriangle, label: 'Rejections', module: 'manufacturing' },
      { to: '/manufacturing/instruments', icon: Gauge, label: 'Instruments', module: 'manufacturing' },
    ],
  },
  {
    label: 'Inputs',
    items: [
      { to: '/inventory', icon: Package, label: 'Materials', module: 'warehouse' },
      { to: '/manufacturing/process-kanban', icon: Route, label: 'Work Centres', module: 'manufacturing' },
    ],
    seeMore: [
      { to: '/manufacturing/shift-control', icon: Gauge, label: 'Shift Control', module: 'manufacturing' },
      { to: '/manufacturing/rm-inspection', icon: CheckCircle, label: 'RM Inspection', module: 'manufacturing' },
      { to: '/manufacturing/floor-inspection', icon: ClipboardCheck, label: 'Floor Inspection', module: 'manufacturing' },
      { to: '/manufacturing/shift-report', icon: FileText, label: 'Shift Reports', module: 'manufacturing' },
      { to: '/manufacturing/scrap', icon: Package, label: 'Scrap & Wastage', module: 'manufacturing' },
      { to: '/qc-templates', icon: ClipboardList, label: 'QC Templates', module: 'manufacturing' },
    ],
  },
];

/** Inventory Manager — D-05 core 8 + See-More */
const INVENTORY_SECTIONS: NavSection[] = [
  {
    label: 'Stock',
    items: [
      { to: '/warehouse/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'warehouse' },
      { to: '/inventory', icon: Warehouse, label: 'Stock', module: 'warehouse' },
      { to: '/warehouse/product-units', icon: Box, label: 'Batches', module: 'warehouse' },
    ],
  },
  {
    label: 'Movements',
    items: [
      { to: '/warehouse/inward', icon: ArrowDownToLine, label: 'Inward Dashboard', module: 'warehouse' },
      { to: '/warehouse/dispatch-dashboard', icon: ArrowUpFromLine, label: 'Dispatch', module: 'warehouse' },
      // Phase 2 plan 09 — N3 one form per truck (dispatch_manager maps to this view via plan 01 roleLayouts)
      { to: '/dispatch-forms', icon: Truck, label: 'Dispatch forms', module: 'warehouse' },
      { to: '/warehouse/transfers', icon: ArrowRightLeft, label: 'Transfer', module: 'warehouse' },
      { to: '/warehouse/gate-passes', icon: ShieldCheck, label: 'Gate passes', module: 'warehouse' },
      { to: '/job-work', icon: ArrowRightLeft, label: 'Job work', module: 'warehouse' },
    ],
  },
  {
    label: 'Warehouse',
    items: [
      { to: '/warehouse-locations', icon: MapPin, label: 'Bins', module: 'warehouse' },
      { to: '/warehouse/stock-audit', icon: ClipboardList, label: 'Cycle Count', module: 'warehouse' },
    ],
    seeMore: [
      { to: '/warehouse/barcode-onboarding', icon: ScanBarcode, label: 'Barcode Onboarding', module: 'warehouse' },
      { to: '/warehouse/barcode-lifecycle', icon: ScanBarcode, label: 'Barcode Lifecycle', module: 'warehouse' },
      { to: '/customer-returns', icon: ArrowRightLeft, label: 'Customer Returns', module: 'warehouse' },
      { to: '/warehouse/cash-rewards', icon: Gift, label: 'Cash Reward Stickers', module: 'logistics' },
      { to: '/products', icon: Package, label: 'Products', module: 'warehouse' },
      { to: '/material-boxes', icon: Box, label: 'Material Boxes', module: 'warehouse' },
    ],
  },
  {
    // Dispatch managers share this menu with store keepers; only they run the service desk.
    label: 'Service',
    items: [
      { to: '/service/sop-dashboard', icon: ClipboardList, label: 'SOP Dashboard', module: 'service-mgmt', roleNamesOnly: ['dispatch_manager'] },
      { to: '/service-tickets', icon: Wrench, label: 'Service Tickets', module: 'service-mgmt', roleNamesOnly: ['dispatch_manager'] },
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt', roleNamesOnly: ['dispatch_manager'] },
    ],
  },
];

/** Procurement Manager — D-06 core 6 + See-More */
const PROCUREMENT_SECTIONS: NavSection[] = [
  {
    label: 'Orders',
    items: [
      { to: '/purchase-dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'procurement' },
      { to: '/purchase-orders', icon: ShoppingCart, label: 'POs', module: 'procurement' },
      { to: '/so-po-mapping', icon: ArrowRightLeft, label: 'SO→PO Mapping', module: 'sales' },
    ],
  },
  {
    label: 'Vendors',
    items: [
      { to: '/vendors', icon: Truck, label: 'Vendors', module: 'procurement' },
      { to: '/job-work/ledger', icon: Scale, label: 'Job-work ledger', module: 'procurement' },
      { to: '/quotation-jobs', icon: FileSearch, label: 'Quotations', module: 'procurement' },
      { to: '/vendor-rate-comparison', icon: Scale, label: 'Rate Comparison', module: 'procurement' },
    ],
    seeMore: [
      { to: '/procurement/landed-cost', icon: Calculator, label: 'Landed Cost', module: 'procurement' },
      { to: '/purchase-contracts', icon: FileSignature, label: 'Purchase Contracts', module: 'procurement' },
      { to: '/transport-contracts', icon: FileSignature, label: 'Transport Contracts', module: 'logistics' },
      { to: '/vendor-returns', icon: ArrowRightLeft, label: 'Vendor Returns', module: 'procurement' },
      { to: '/purchase-requests', icon: FileSearch, label: 'Purchase Requests', module: 'procurement' },
    ],
  },
  // Apr 28 evening — Products (master catalog) was missing entirely from
  // the Procurement view. POs reference products on every line item, so
  // procurement managers need direct access. Products page handles both
  // create + browse + edit on /products.
  {
    label: 'Catalog',
    items: [
      { to: '/products', icon: Package, label: 'Products' },
    ],
  },
];

/** Project Manager — core 7 + See-More */
const PROJECT_SECTIONS: NavSection[] = [
  {
    label: 'Delivery',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/projects', icon: FolderKanban, label: 'Projects', module: 'projects' },
      { to: '/projects?view=gantt', icon: CalendarDays, label: 'Gantt', module: 'projects' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/tasks?view=timesheets', icon: ClipboardList, label: 'Timesheets' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/analytics?view=projects', icon: BarChart3, label: 'P&L', module: 'analytics' },
      { to: '/projects?view=milestones', icon: CheckCircle, label: 'Milestones', module: 'projects' },
    ],
    seeMore: [
      { to: '/service/itbp-installations', icon: Wrench, label: 'ITBP Installations', module: 'service-mgmt' },
      { to: '/field-partners', icon: UserCog, label: 'Field Partners Hub' },
    ],
  },
];

/** Service Manager — not in the D-11 spec but exists as a role */
const SERVICE_SECTIONS: NavSection[] = [
  {
    label: 'Service',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/service/sop-dashboard', icon: ClipboardList, label: 'SOP Dashboard', module: 'service-mgmt' },
      { to: '/service-tickets', icon: Wrench, label: 'Service Tickets', module: 'service-mgmt' },
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
    ],
  },
  {
    label: 'Rules',
    items: [
      { to: '/escalation-rules', icon: AlertTriangle, label: 'Escalation Rules', module: 'service-mgmt' },
    ],
    seeMore: [
      { to: '/service/itbp-installations', icon: Wrench, label: 'ITBP Installations', module: 'service-mgmt' },
      // L229-WEB-3 — Support Desk is desk-roles-only (mirrors backend SUPPORT_DESK_ROLES).
      { to: '/support/tickets', icon: Headphones, label: 'System Support', roleNamesOnly: ['admin', 'owner', 'super_admin'] },
      { to: '/support/bug-tests', icon: FlaskConical, label: 'Bug Test Dashboard', roleNamesOnly: ['admin', 'owner', 'super_admin'] },
    ],
  },
];

/** Telecaller */
const TELECALLER_SECTIONS: NavSection[] = [
  {
    label: 'Performance',
    items: [{ to: '/scorecard', icon: Target, label: 'My Scorecard' }],
  },
  {
    label: 'Calling',
    items: [
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin'] },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      { to: '/offers', icon: FileSignature, label: 'Offers' },
      // Pool Heat Pump Calculator — module-gated, only renders for Sun
      // Stellar (per tenant.enabledModules). Telecallers can pull this up
      // mid-call with the customer on the line. See Track D PERSONA.md.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** HR */
const HR_SECTIONS: NavSection[] = [
  {
    label: 'People',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/employees', icon: UserCog, label: 'Employees', module: 'hr' },
      // LBR2-D3 (2026-07-26) — recruitment dashboard (see OWNER_SECTIONS note).
      { to: '/hr/recruitment', icon: Briefcase, label: 'Recruitment', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'], capability: 'recruitment' },
      { to: '/hr/recruitment/partners', icon: Briefcase, label: 'Local partners', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'], capability: 'recruitment' },
      // L200 Sheet 1 (2026-07-16) — same row, same gate as OWNER_SECTIONS.
      // NOTE: today an `hr`-role user does NOT actually land on this view —
      // resolveRoleKey has no `hr` branch, so they fall through to OWNER and get
      // OWNER_SECTIONS (pre-existing quirk, deliberately not fixed here). This
      // view is reachable via the admin topbar dept-override, and the row is
      // placed so that payroll doesn't silently vanish if that branch is added.
      { to: '/payroll', icon: IndianRupee, label: 'Payroll', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      { to: '/expense-approvals', icon: Wallet, label: 'Expense Approvals', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant'] },
      { to: '/leave-requests', icon: CalendarDays, label: 'Leave' },
      { to: '/incentives', icon: IndianRupee, label: 'Incentives', module: 'hr' },
    ],
  },
  // 2026-10-07 (Manak) Apps & Training.
  {
    label: 'Apps & Training',
    items: [
      { to: '/hr/mobile-apps', icon: Smartphone, label: 'Mobile Apps', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'] },
      { to: '/hr/app-tour-progress', icon: GraduationCap, label: 'App Tour Progress', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'] },
      // 2026-10-08 (Manak): HR's own App Tour (the M18 "HR and admin" lessons).
      { to: '/tour', icon: GraduationCap, label: 'App Tour', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr'] },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Executive sidebars (Apr 22, 2026 exec sprint — trimmed Core 3–5)   */
/* ------------------------------------------------------------------ */

/**
 * Sales Executive — Core 5 + See-More.
 *
 * Apr 28, 2026 — telecaller fully merged into this role. The previous
 * standalone TELECALLER_SECTIONS is deprecated; sales people get all the
 * calling tools (Today queue, Call Logs, My Calls, Leads, Offers) PLUS
 * their dealer/incentive/history surface. The CallBar bottom strip is
 * gated to this role + SALES_MANAGER + TELECALLER (legacy alias).
 */
const SALES_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    // K7 Scorecard — Jun 2026. Exec sees My Scorecard only.
    label: 'Performance',
    items: [
      { to: '/scorecard', icon: Target, label: 'My Scorecard' },
    ],
  },
  {
    label: 'Calling',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin', 'sales_executive'] },
      { to: '/executive/sales/call-queue', icon: Phone, label: 'Call Queue' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      // v6.3.7 — Promoted to top-level so it doesn't get dropped by the
      // last-section-only seeMore guard (Sidebar.tsx:968). Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
    seeMore: [
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      { to: '/offers', icon: FileSignature, label: 'Offers' },
      { to: '/executive/sales/history', icon: ScrollText, label: 'Call History' },
    ],
  },
  {
    label: 'My Pipeline',
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      // 2026-10-08 (Manak): incentives are never shown to employees — item removed.
      // CVG3-DISC (L103, 2026-07-11) — coordinators resolve to THIS view
      // (resolveRoleKey: sales dept, no manager/head title → SALES_EXECUTIVE),
      // so neither existing Discount entry (OWNER Revenue / SALES see-more) is
      // ever visible to them. Read/track queue: raise + status; Approve/Reject
      // stays manager+ (canDecideDiscountRole page gate). roleNamesOnly keeps
      // this invisible to ordinary sales executives — no scope widening.
      { to: '/discount-requests', icon: Percent, label: 'Discount Requests', module: 'sales', roleNamesOnly: ['coordinator', 'sales_coordinator'] },
    ],
  },
];

/** Service Technician — Core 4 + See-More */
const SERVICE_TECHNICIAN_SECTIONS: NavSection[] = [
  {
    label: 'My Route',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/service/tickets', icon: Wrench, label: 'My Tickets' },
      { to: '/executive/service/parts-expenses', icon: Package, label: 'Parts & Expenses' },
      { to: '/service-tickets', icon: ClipboardList, label: 'All Tickets', module: 'service-mgmt' },
    ],
    seeMore: [
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
];

/** Production Operator — Core 3 + See-More */
const PRODUCTION_OPERATOR_SECTIONS: NavSection[] = [
  {
    label: 'My Shift',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/production/work-orders', icon: Factory, label: 'Work Orders' },
      { to: '/executive/production/qc-checkpoints', icon: ClipboardCheck, label: 'QC Checkpoints' },
    ],
    seeMore: [
      { to: '/manufacturing/rm-inspection', icon: CheckCircle, label: 'RM Inspection', module: 'manufacturing' },
      { to: '/manufacturing/shift-report', icon: FileText, label: 'Shift Report', module: 'manufacturing' },
    ],
  },
];

/** Warehouse Executive — Core 3 + See-More */
const WAREHOUSE_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'My Floor',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/warehouse/grn', icon: ArrowDownToLine, label: 'GRN Queue' },
      { to: '/executive/warehouse/dispatch', icon: ArrowUpFromLine, label: 'My Dispatch' },
    ],
    seeMore: [
      { to: '/warehouse/stock-audit', icon: ClipboardList, label: 'Stock Audit', module: 'warehouse' },
      { to: '/warehouse/barcode-lifecycle', icon: ScanBarcode, label: 'Barcode Lifecycle', module: 'warehouse' },
    ],
  },
];

/** Accounts Executive — Core 3 + See-More */
const ACCOUNTS_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'My Desk',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/accounts/match-queue', icon: ArrowRightLeft, label: 'Match Queue' },
      { to: '/executive/accounts/approvals', icon: ShieldCheck, label: 'Approvals' },
    ],
    seeMore: [
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
    ],
  },
];

/** Field Partner — Core 3 + See-More */
const FIELD_PARTNER_SECTIONS: NavSection[] = [
  {
    label: 'My Work',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/field-partner/claims', icon: ClipboardList, label: 'My Claims' },
      { to: '/executive/field-partner/payouts', icon: Wallet, label: 'My Payouts' },
    ],
    seeMore: [
      { to: '/field-partners', icon: UserCog, label: 'Field Partners' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Apr 26, 2026 (Squad-B) — Sales role re-bifurcation                  */
/*                                                                      */
/*  SALES_EXECUTIVE_SECTIONS above is the merged legacy layout (kept    */
/*  for back-compat). The two configs below split the role into:        */
/*    - field_executive: own/assigned leads only                        */
/*    - backend_staff:   team's leads (tele sales)                      */
/* ------------------------------------------------------------------ */

/** Field Executive — sees own/assigned leads only */
const FIELD_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'Performance',
    items: [{ to: '/scorecard', icon: Target, label: 'My Scorecard' }],
  },
  {
    label: 'My Day',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/leads', icon: Download, label: 'My Leads', module: 'lead-integrations' },
      { to: '/executive/sales/call-queue', icon: Phone, label: "Today's Call Queue" },
      { to: '/calls', icon: MapPin, label: 'Visit Planner' },
      // Field Sessions Phase 2 (2026-07-29). The RECORD of the day, distinct
      // from the Visit Planner above, which is the manager-built PLAN.
      { to: '/field-sessions', icon: MapPin, label: 'Field Sessions' },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/call-logs', icon: ScrollText, label: 'Call History' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/leads?intent=add', icon: Download, label: 'Add Lead' },
      // Pool Heat Pump Calculator — for field execs to size pools at site
      // visits / walk-in customers. Module-gated to Sun Stellar tenant.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Backend Staff / Tele Sales — sees the team's leads */
const BACKEND_STAFF_SECTIONS: NavSection[] = [
  {
    label: 'Performance',
    items: [{ to: '/scorecard', icon: Target, label: 'My Scorecard' }],
  },
  {
    label: 'Calling',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin', 'backend_staff', 'backend_crm', 'tele_sales'] },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
  {
    label: 'Lead Pipeline',
    items: [
      { to: '/leads?scope=team', icon: Download, label: 'Team Leads', module: 'lead-integrations' },
      { to: '/leads?intent=add', icon: Download, label: 'Add Lead' },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/sales/activities', icon: Activity, label: 'Activities' },
      { to: '/offers', icon: FileSignature, label: 'Quote Requests' },
      // Pool Heat Pump Calculator — backend staff / tele-sales surface.
      // Module-gated to Sun Stellar tenant.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Lead Manager — verification + assignment workflow (Apr 26 → Apr 28, 2026)
 *  Apr 28 — re-pointed all entries to the comprehensive /lead-manager/* page
 *  set (12 screens) shipped by Squad-D. The old /leads/verified +
 *  /leads/unverified stubs from Squad-B have hardcoded "endpoint not online"
 *  fallbacks and should not be linked from any sidebar. */
const LEAD_MANAGER_SECTIONS: NavSection[] = [
  {
    label: 'Inbox',
    items: [
      { to: '/lead-manager', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/lead-manager/unverified', icon: ShieldCheck, label: 'Unverified Queue' },
      { to: '/lead-manager/verified', icon: CheckCircle, label: 'Verified Pool' },
      { to: '/lead-manager/metrics', icon: CalendarDays, label: "Today's Verifications" },
    ],
  },
  {
    label: 'Assignment',
    items: [
      { to: '/lead-manager/verified', icon: Users, label: 'Verified Leads' },
      { to: '/lead-manager/assigned', icon: Handshake, label: 'Already-Assigned Oversight' },
      { to: '/lead-manager/reassignments', icon: ScrollText, label: 'Reassignment Requests' },
      // L239 (2026-09-03) — lead_manager is in the dealer manager tier (own
      // dealers only, backend dealer-access.ts). Gate = shared DEALER_PAGE_ROLES.
      { to: '/dealer-management', icon: Handshake, label: 'My dealers', module: 'lead-integrations', roleNamesOnly: DEALER_PAGE_ROLES },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'All Contacts', module: 'crm' },
      { to: '/lead-manager/search', icon: FileSearch, label: 'Search' },
      { to: '/lead-manager/settings', icon: ScrollText, label: 'Lead Manager Settings' },
      // Pool Heat Pump Calculator — Lead Manager surface, useful when sizing
      // a verified lead's project before assignment. Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Lead Triage Coordinator (Deepika role) — May 26, 2026.
 *  STRICT view: only the triage dashboard + her own profile. No CRM, no
 *  finance, no /dashboard, no global search across contacts/deals/invoices.
 *  Permission gating is enforced server-side via requirePermission(); this
 *  sidebar mirrors it for visibility consistency. */
const LEAD_TRIAGE_SECTIONS: NavSection[] = [
  {
    label: 'Inbox',
    items: [
      { to: '/leads/triage', icon: Download, label: 'Lead Triage' },
      // L239 (2026-09-03) — triage tier can open the Dealers page (dealer
      // directory + assign-to-manager; backend dealer-access.ts triage tier).
      // Deliberate widening of the strict view. Gate = shared DEALER_PAGE_ROLES.
      { to: '/dealer-management', icon: Handshake, label: 'Dealers', module: 'lead-integrations', roleNamesOnly: DEALER_PAGE_ROLES },
    ],
  },
  {
    // K7 KPI dashboards — Jun 2026. Lead triage coordinator sees Lead Ops.
    label: 'Performance',
    items: [
      { to: '/lead-ops', icon: ClipboardList, label: 'Lead Ops' },
    ],
  },
  {
    label: 'You',
    items: [
      { to: '/profile', icon: UserCog, label: 'Profile' },
    ],
  },
];

/** Super-admin — only for admin@aura.ardhanex.com */
const SUPER_ADMIN_SECTIONS: NavSection[] = [
  {
    label: 'Platform',
    items: [
      { to: '/admin', icon: Crown, label: 'Platform Dashboard' },
      { to: '/admin/tenants', icon: Building2, label: 'Tenant Management' },
      { to: '/admin/billing', icon: IndianRupee, label: 'Billing' },
      { to: '/admin/health', icon: Server, label: 'System Health' },
      { to: '/admin/team', icon: Users, label: 'Team' },
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
      { to: '/admin/resources', icon: Gauge, label: 'Resources' },
      { to: '/admin/carrier-numbers', icon: Phone, label: 'Carrier Numbers' },
    ],
  },
];

export const SECTIONS_BY_VIEW: Record<SidebarView, NavSection[]> = {
  unknown: [],
  crm_operations: [],
  qa_manager: [],
  qa_inspector: [],
  dispatch_manager: [],
  loader: [],
  security: [],
  puf_operator: [],
  packing_operator: [],

  owner: OWNER_SECTIONS,
  sales: SALES_SECTIONS,
  accountant: ACCOUNTANT_SECTIONS,
  production: PRODUCTION_SECTIONS,
  inventory: INVENTORY_SECTIONS,
  procurement: PROCUREMENT_SECTIONS,
  project: PROJECT_SECTIONS,
  service: SERVICE_SECTIONS,
  telecaller: TELECALLER_SECTIONS,
  hr: HR_SECTIONS,
  lead_manager: LEAD_MANAGER_SECTIONS,
  lead_triage: LEAD_TRIAGE_SECTIONS,
  super_admin: SUPER_ADMIN_SECTIONS,
  // Executive views
  sales_executive: SALES_EXECUTIVE_SECTIONS,
  service_technician: SERVICE_TECHNICIAN_SECTIONS,
  production_operator: PRODUCTION_OPERATOR_SECTIONS,
  warehouse_executive: WAREHOUSE_EXECUTIVE_SECTIONS,
  accounts_executive: ACCOUNTS_EXECUTIVE_SECTIONS,
  field_partner: FIELD_PARTNER_SECTIONS,
  // Apr 26, 2026 (Squad-B) — bifurcated sales sub-roles
  field_executive: FIELD_EXECUTIVE_SECTIONS,
  backend_staff: BACKEND_STAFF_SECTIONS,
};

// 2026-09-16 amendment: prepend only for an explicit sales identity.
export const SALES_ACTIVITY_SECTION: NavSection = {
  label: 'My Work',
  items: [{ to: '/lead-activity', icon: Activity, label: 'Lead Activity' }],
};

/* ------------------------------------------------------------------ */
/*  View resolver — DashboardRoleKey → SidebarView                    */
/* ------------------------------------------------------------------ */

export function roleKeyToView(roleKey: DashboardRoleKey, roleName?: string): SidebarView {
  const profileViews: Partial<Record<DashboardRoleKey, SidebarView>> = { HR: 'hr', UNKNOWN: 'unknown', CRM_OPERATIONS: 'crm_operations', QA_MANAGER: 'qa_manager', QA_INSPECTOR: 'qa_inspector', DISPATCH_MANAGER: 'dispatch_manager', LOADER: 'loader', SECURITY: 'security', PUF_OPERATOR: 'puf_operator', PACKING_OPERATOR: 'packing_operator' };
  if (profileViews[roleKey]) return profileViews[roleKey]!;
  // Tenant admin (owner) → owner (D-07)
  if (roleName === 'admin') return 'owner';
  // May 26, 2026 — Lead Triage Coordinator (Deepika). Strict-view gate.
  // Resolved BEFORE every other check so it wins for the dedicated role.
  if (roleName === 'lead_triage_coordinator') return 'lead_triage';
  if ((roleKey as string) === 'LEAD_TRIAGE_COORDINATOR') return 'lead_triage';
  // Apr 26, 2026 (Squad-B) — LEAD_MANAGER + BACKEND_STAFF resolve from
  // role.name BEFORE the formal roleKey switch, since DashboardRoleKey may
  // not have been widened by Squad-A yet at compile time.
  if (roleName === 'lead_manager') return 'lead_manager';
  if (roleName === 'backend_staff' || roleName === 'backend_crm' || roleName === 'tele_sales') return 'backend_staff';
  if (roleName === 'field_executive') return 'field_executive';
  // String-cast roleKey check so newer enum values (added by Squad-A) work.
  if ((roleKey as string) === 'LEAD_MANAGER') return 'lead_manager';
  if ((roleKey as string) === 'BACKEND_STAFF') return 'backend_staff';
  // Finance manager / accountant → accountant
  if (roleKey === 'FINANCE_MANAGER') return 'accountant';
  // Sales Manager / Head → sales
  if (roleKey === 'SALES_HEAD' || roleKey === 'SALES_MANAGER') return 'sales';
  // Sales Executive → sales_executive
  // (Apr 24, 2026 — field/office merged into a single SALES_EXECUTIVE key.)
  if (roleKey === 'SALES_EXECUTIVE') return 'sales_executive';
  // Production Head → production
  if (roleKey === 'PRODUCTION_HEAD') return 'production';
  // Production Supervisor / Operator → production_operator
  if (roleKey === 'PRODUCTION_SUPERVISOR') return 'production_operator';
  // Inventory
  if (roleKey === 'INVENTORY_HEAD') return 'inventory';
  // Procurement
  if (roleKey === 'PURCHASE_HEAD') return 'procurement';
  // Service Manager → service (manager sidebar)
  if (roleKey === 'SERVICE_MANAGER') return 'service';
  // Service Technician → service_technician (trimmed exec sidebar)
  if (roleKey === 'SERVICE_TECHNICIAN') return 'service_technician';
  // Telecaller → sales_executive (shares call-queue-driven workflow; legacy telecaller sidebar kept for admin override)
  if (roleKey === 'TELECALLER') return 'sales_executive';
  // Warehouse Executive → warehouse_executive (trimmed exec sidebar)
  if (roleKey === 'WAREHOUSE_EXECUTIVE') return 'warehouse_executive';
  // Accounts Executive → accounts_executive (trimmed exec sidebar)
  if (roleKey === 'ACCOUNTS_EXECUTIVE') return 'accounts_executive';
  // Field Partner → field_partner (trimmed exec sidebar)
  if (roleKey === 'FIELD_PARTNER') return 'field_partner';
  return roleKey === 'OWNER' ? 'owner' : 'unknown';
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Filter an item list by enabled modules and role-name gates. */
const PLAN_ORDER: Record<string, number> = {
  FREE: 0, STARTER: 1, GROWTH: 2, ENTERPRISE: 3,
};

export function filterItems(
  items: NavItem[],
  enabledModules: Set<string> | null,
  roleName: string,
  planTier: string | null,
  capabilities?: { discountUi: boolean; recruitment: boolean } | null,
  permissions?: Record<string, boolean | undefined> | null,
): NavItem[] {
  return items.filter((item) => {
    // Phase 5 permission gate — default HIDE, and `=== true` so an absent key,
    // a null map or an in-flight fetch all read as "not permitted".
    if (item.requiresPermission && permissions?.[item.requiresPermission] !== true) return false;
    if (item.roleNamesOnly && !item.roleNamesOnly.includes(roleName)) return false;
    if (item.module && enabledModules && !enabledModules.has(item.module)) return false;
    // LBR2-G1 — capability gate (default OFF: hide unless explicitly enabled).
    if (item.capability && capabilities?.[item.capability] !== true) return false;
    if (item.planGate) {
      const tenantLevel = PLAN_ORDER[planTier ?? 'FREE'] ?? 0;
      const requiredLevel = PLAN_ORDER[item.planGate] ?? 0;
      if (tenantLevel < requiredLevel) return false;
    }
    return true;
  });
}

const DEALER_MANAGEMENT_PATH = '/dealer-management';

const DEALER_PROSPECTS_PATH = '/dealer-prospects';

/**
 * 2026-09-24 — the dealer destination is TWO rows: the dealers page and, beside
 * it, Prospect dealers (shops we want to make dealers). Same gate for both.
 */
function dealerManagementItems(tier: Exclude<DealerTier, null>): NavItem[] {
  return [
    {
      to: DEALER_MANAGEMENT_PATH,
      icon: Handshake,
      label: tier === 'manager' ? 'My dealers' : 'Dealers',
      module: 'lead-integrations',
      roleNamesOnly: DEALER_PAGE_ROLES,
    },
    {
      to: DEALER_PROSPECTS_PATH,
      icon: Users,
      label: 'Prospect dealers',
      module: 'lead-integrations',
      roleNamesOnly: DEALER_PAGE_ROLES,
    },
  ];
}

/**
 * Keep dealer-page authorization independent from dashboard layout selection.
 * Existing role layouts retain their original shape; this pass normalizes any
 * existing dealer row to the caller's tier, removes duplicates/legacy aliases,
 * and adds one standard row when a legitimate specialized layout has none.
 * The normal module and exact-role filters still run afterwards.
 */
export function withDealerManagementNavigation(
  baseSections: NavSection[],
  tier: DealerTier,
): NavSection[] {
  let inserted = false;

  const normalizeItems = (items: NavItem[] | undefined): NavItem[] | undefined => {
    if (!items) return undefined;
    return items.flatMap((item) => {
      const isDealerDestination =
        item.to === DEALER_MANAGEMENT_PATH || item.to === DEALER_PROSPECTS_PATH || item.to === '/dealer-leads';
      if (!isDealerDestination) return [item];
      if (tier === null || inserted) return [];
      inserted = true;
      return dealerManagementItems(tier);
    });
  };

  const sections: NavSection[] = baseSections.map((section) => ({
    ...section,
    items: normalizeItems(section.items) ?? [],
    seeMore: normalizeItems(section.seeMore),
  }));

  if (tier !== null && !inserted) {
    const dealerSection: NavSection = {
      label: 'Dealers',
      items: dealerManagementItems(tier),
    };
    const youIndex = sections.findIndex((section) => section.label === 'You');
    sections.splice(youIndex === -1 ? sections.length : youIndex, 0, dealerSection);
  }

  return sections;
}

/** Strip any query-string for NavLink matching (we pass `?tab=ledger` in `to`). */
export function routeOnly(to: string): string {
  const q = to.indexOf('?');
  return q === -1 ? to : to.slice(0, q);
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export const SIDEBAR_VIEWS: { id: SidebarView; label: string }[] = [
  // Managers
  { id: 'owner', label: VIEW_LABELS.owner },
  { id: 'sales', label: VIEW_LABELS.sales },
  { id: 'lead_manager', label: VIEW_LABELS.lead_manager },
  { id: 'lead_triage', label: VIEW_LABELS.lead_triage },
  { id: 'accountant', label: VIEW_LABELS.accountant },
  { id: 'production', label: VIEW_LABELS.production },
  { id: 'inventory', label: VIEW_LABELS.inventory },
  { id: 'procurement', label: VIEW_LABELS.procurement },
  { id: 'project', label: VIEW_LABELS.project },
  { id: 'service', label: VIEW_LABELS.service },
  { id: 'telecaller', label: VIEW_LABELS.telecaller },
  { id: 'hr', label: VIEW_LABELS.hr },
  // Executives (Apr 22, 2026 exec sprint)
  { id: 'sales_executive', label: VIEW_LABELS.sales_executive },
  { id: 'field_executive', label: VIEW_LABELS.field_executive },
  { id: 'backend_staff', label: VIEW_LABELS.backend_staff },
  { id: 'service_technician', label: VIEW_LABELS.service_technician },
  { id: 'production_operator', label: VIEW_LABELS.production_operator },
  { id: 'warehouse_executive', label: VIEW_LABELS.warehouse_executive },
  { id: 'accounts_executive', label: VIEW_LABELS.accounts_executive },
  { id: 'field_partner', label: VIEW_LABELS.field_partner },
];

/**
 * Grouped views for TopBar's split "View as" dropdown (Apr 22, 2026 exec sprint).
 * TopBar renders these as two sections with headings: MANAGERS / EXECUTIVES.
 */
export const SIDEBAR_VIEW_GROUPS: {
  heading: string;
  views: { id: SidebarView; label: string }[];
}[] = [
  {
    heading: 'MANAGERS',
    views: [
      { id: 'owner', label: VIEW_LABELS.owner },
      { id: 'sales', label: VIEW_LABELS.sales },
      // Apr 26 2026 (Squad-B) — Lead Manager added between Sales and Accountant.
      { id: 'lead_manager', label: VIEW_LABELS.lead_manager },
      // May 26 2026 — Lead Triage Coordinator (Deepika) — strict-view role.
      { id: 'lead_triage', label: VIEW_LABELS.lead_triage },
      { id: 'accountant', label: VIEW_LABELS.accountant },
      { id: 'production', label: VIEW_LABELS.production },
      { id: 'inventory', label: VIEW_LABELS.inventory },
      { id: 'procurement', label: VIEW_LABELS.procurement },
      { id: 'project', label: VIEW_LABELS.project },
      { id: 'service', label: VIEW_LABELS.service },
      { id: 'hr', label: VIEW_LABELS.hr },
      // Apr 28 2026 — TeleCaller removed from MANAGERS group; merged into
      // Sales Executive role (telecaller is a sales-exec function, not a
      // manager role). Telecaller calling features now live inside the
      // Sales Executive sidebar (Today / Call Logs / My Calls / Leads / Offers).
    ],
  },
  {
    heading: 'EXECUTIVES',
    views: [
      { id: 'sales_executive', label: VIEW_LABELS.sales_executive },
      // Apr 26 2026 (Squad-B) — sales executive bifurcation.
      { id: 'field_executive', label: VIEW_LABELS.field_executive },
      { id: 'backend_staff', label: VIEW_LABELS.backend_staff },
      { id: 'service_technician', label: VIEW_LABELS.service_technician },
      { id: 'production_operator', label: VIEW_LABELS.production_operator },
      { id: 'warehouse_executive', label: VIEW_LABELS.warehouse_executive },
      { id: 'accounts_executive', label: VIEW_LABELS.accounts_executive },
      { id: 'field_partner', label: VIEW_LABELS.field_partner },
    ],
  },
];
